import { useEffect, useRef } from 'react';

import { subscribeSpotIndia, unsubscribeSpotIndia } from 'epics/spot';
import { isSpotModalOpenSelector } from 'selectors/spotSelector';
import {
  spotIndiaSymbolsSelector,
  spotIndiaSymbolsSelectorWithBalance,
  spotWalletBalanceList,
} from 'selectors/walletSelector';
import { useAppDispatch, useAppSelector } from 'storeHooks';

const useSpotSubscription = () => {
  const dispatch = useAppDispatch();

  const symbols = useAppSelector(spotIndiaSymbolsSelector);
  const symbolsWithBalance = useAppSelector(spotIndiaSymbolsSelectorWithBalance);
  const spotWalletBalances = useAppSelector(spotWalletBalanceList);
  const isSpotModalOpen = useAppSelector(isSpotModalOpenSelector);

  const isSubscribedRef = useRef(false);
  const isSubscribedFromOutSideRef = useRef(false);
  const previousSymbolsWithBalanceRef = useRef<Record<string, any> | null>(null);

  useEffect(() => {
    const hasSymbols = Object.values(symbols).length > 0;
    const hasSymbolsWithBalance = Object.values(symbolsWithBalance || {}).length > 0;

    if (!hasSymbols) {
      return () => {};
    }

    // Check if symbolsWithBalance has changed
    const symbolsWithBalanceChanged =
      JSON.stringify(previousSymbolsWithBalanceRef.current) !==
      JSON.stringify(symbolsWithBalance);

    if (isSpotModalOpen) {
      if (!isSubscribedRef.current) {
        dispatch(unsubscribeSpotIndia());
        dispatch(subscribeSpotIndia());
        isSubscribedRef.current = true;
        isSubscribedFromOutSideRef.current = false;
        previousSymbolsWithBalanceRef.current = null;
      }
      return () => {};
    }

    // When modal is closed, handle subscription based on symbols with balance
    if (hasSymbolsWithBalance) {
      // Re-subscribe if symbols with balance changed or not yet subscribed
      if (symbolsWithBalanceChanged || !isSubscribedFromOutSideRef.current) {
        dispatch(unsubscribeSpotIndia());
        dispatch(subscribeSpotIndia(symbolsWithBalance));
        isSubscribedFromOutSideRef.current = true;
        previousSymbolsWithBalanceRef.current = symbolsWithBalance;
      }
    } else if (isSubscribedFromOutSideRef.current) {
      // Unsubscribe if no symbols with balance and currently subscribed
      dispatch(unsubscribeSpotIndia());
      isSubscribedFromOutSideRef.current = false;
      previousSymbolsWithBalanceRef.current = null;
    } else if (!isSpotModalOpen) {
      dispatch(unsubscribeSpotIndia());
      isSubscribedRef.current = false;
      isSubscribedFromOutSideRef.current = false;
      previousSymbolsWithBalanceRef.current = null;
    }

    return () => {
      if (isSubscribedRef.current) {
        dispatch(unsubscribeSpotIndia());
        isSubscribedRef.current = false;
        isSubscribedFromOutSideRef.current = false;
        previousSymbolsWithBalanceRef.current = null;
      }
    };
  }, [isSpotModalOpen, symbols, symbolsWithBalance, spotWalletBalances, dispatch]);
};

export { useSpotSubscription };
